import { generateClient } from 'aws-amplify/api';
import { getSession } from '../utils/session';

// Lazy-initialize the client to ensure Amplify is configured first
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _client: any = null;

function getClient() {
  if (!_client) {
    _client = generateClient();
  }
  return _client;
}

// Request deduplication with TTL cache
// - In-flight requests share the same promise
// - Recently completed requests return cached result (prevents rapid duplicate calls)
const inflightRequests = new Map<string, Promise<unknown>>();
const resultCache = new Map<string, { data: unknown; timestamp: number }>();
const CACHE_TTL_MS = 2000; // Cache results for 2 seconds

function getRequestKey(queryString: string, variables?: Record<string, unknown>, authMode?: string): string {
  // Use a hash of the full query + variables for more reliable deduplication
  // Extract operation name from query for readability
  const opMatch = queryString.match(/(?:query|mutation)\s+(\w+)/);
  const opName = opMatch?.[1] || 'unknown';
  return JSON.stringify({ op: opName, v: variables, a: authMode });
}

async function deduplicatedRequest<T>(
  key: string,
  executor: () => Promise<T>
): Promise<T> {
  // Check if we have a recent cached result
  const cached = resultCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data as T;
  }

  // If this exact request is already in-flight, return the same promise
  const existing = inflightRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  // Execute and cache the promise
  const promise = executor()
    .then((result) => {
      // Cache the successful result
      resultCache.set(key, { data: result, timestamp: Date.now() });
      return result;
    })
    .finally(() => {
      // Clean up in-flight tracking
      inflightRequests.delete(key);

      // Clean up old cache entries periodically
      if (resultCache.size > 50) {
        const now = Date.now();
        for (const [k, v] of resultCache) {
          if (now - v.timestamp > CACHE_TTL_MS) {
            resultCache.delete(k);
          }
        }
      }
    });

  inflightRequests.set(key, promise);
  return promise;
}

// Helper function to execute queries (uses default auth - userPool if authenticated)
// Note: Relies on AuthContext to prime the session cache at app startup
export async function query<T>(
  queryString: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const key = getRequestKey(queryString, variables, 'userPool');

  return deduplicatedRequest(key, async () => {
    // Pre-warm credentials to prevent duplicate Cognito calls
    await getSession();

    const result = await getClient().graphql({
      query: queryString,
      variables,
    });

    if ('data' in result) {
      return result.data as T;
    }

    throw new Error('GraphQL query failed');
  });
}

// Helper function for public queries (uses API_KEY auth - no login required)
export async function publicQuery<T>(
  queryString: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const key = getRequestKey(queryString, variables, 'apiKey');

  return deduplicatedRequest(key, async () => {
    // Pre-warm credentials even for apiKey mode (Amplify still fetches internally)
    await getSession();

    const result = await getClient().graphql({
      query: queryString,
      variables,
      authMode: 'apiKey',
    });

    if ('data' in result) {
      return result.data as T;
    }

    throw new Error('GraphQL query failed');
  });
}

// Helper function to execute mutations (no deduplication - mutations should always execute)
export async function mutate<T>(
  mutationString: string,
  variables?: Record<string, unknown>
): Promise<T> {
  // Pre-warm credentials to prevent duplicate Cognito calls
  await getSession();

  const result = await getClient().graphql({
    query: mutationString,
    variables,
  });

  if ('data' in result) {
    return result.data as T;
  }

  throw new Error('GraphQL mutation failed');
}
